import { Box, Button, CircularProgress, MenuItem, Select, Typography, Stack, Tooltip, IconButton, TextField, InputAdornment, Divider, Dialog, DialogContent, Fab, Checkbox } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { useEffect, useState } from "react";
import { showSnackbar } from "../../../redux/actions";
import axios from "axios";
import { urlAPI } from "../../../config/config";
import { useParams } from "react-router";

// Icons
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import TableChartIcon from '@mui/icons-material/TableChart';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from '@mui/icons-material/Close';

// Tools & Components
import SalariesData from "./SalariesData";
import SalariesModals from "./SalariesModals"; // Contient la logique interne des modales si besoin
import getForecastSalariesData from "../../../tools/forecast/getForecastSalariesData";
import getSalarialMatrice from "../../../tools/forecast/getSalarialMatrice";
import { generateForecastSalaries } from "../../../tools/forecast/generateForecastSalaries";
import { duplicateSalary } from "../../../tools/forecast/duplicateSalary";
import AlerteDialog from "../../AlerteDialog/AlerteDialog";

const Salaries = ({ forecastVersion, forecastDates }: { forecastVersion: number | undefined, forecastDates: string }) => {
    const dispatch = useDispatch();
    const params = useParams();
    const token = useSelector((state: RootState) => state.user.token);

    // --- ÉTATS ---
    const [loading, setLoading] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [openMatriceModal, setOpenMatriceModal] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    
    const [editId, setEditId] = useState<string>("");
    const [selectedSalaries, setSelectedSalaries] = useState<string[]>([]);
    const [salariesData, setSalariesData] = useState<any[]>([]);
    const [salarialMatrice, setSalarialMatrice] = useState<any>();

    // Filtres
    const [agency, setAgency] = useState<string>("All");
    const [searchName, setSearchName] = useState<string>("");

    useEffect(() => {
        if (token) {
            getForecastSalariesData(params.id, forecastVersion, agency, setSalariesData, token, dispatch);
            getSalarialMatrice(params.id, forecastVersion, setSalarialMatrice, token);
        }
    }, [forecastVersion, agency, openModal, openMatriceModal, showDeleteDialog]);

    // --- ACTIONS ---
    const confirmDelete = async () => {
        try {
            await Promise.all(selectedSalaries.map(id => 
                axios.delete(`${urlAPI}/forecast/salaries/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            ));
            setShowDeleteDialog(false);
            setSelectedSalaries([]);
            dispatch(showSnackbar("Salariés supprimés avec succès.", "success"));
        } catch (error) {
            dispatch(showSnackbar("Erreur lors de la suppression.", "error"));
        }
    };

    const handleDuplication = () => {
        duplicateSalary(selectedSalaries[0], token, setOpenModal, dispatch);
        setSelectedSalaries([]);
    };

    return (
        <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
            
            {/* --- BARRE DE FILTRES (Style Turnover) --- */}
            <Box sx={{ bgcolor: "white", p: 3, borderRadius: 4, mb: 4, border: "1px solid #e2e8f0", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>👥 Salariés</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                            size="small" 
                            startIcon={<SyncIcon />} 
                            onClick={() => generateForecastSalaries(forecastDates, setSalariesData, dispatch, params.id, forecastVersion, token)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Sync Dolibarr
                        </Button>
                        <Button 
                            size="small" 
                            startIcon={<TableChartIcon />} 
                            onClick={() => setOpenMatriceModal(true)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Matrice
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                    {/* Agence */}
                    <Select 
                        value={agency} 
                        onChange={(e) => setAgency(e.target.value)} 
                        size="small" 
                        sx={{ minWidth: 160, borderRadius: 2, bgcolor: "#f1f5f9", "& fieldset": { border: "none" }, fontWeight: 600 }}
                    >
                        <MenuItem value="All">Toutes les agences</MenuItem>
                        <MenuItem value="Nord">Nord</MenuItem>
                        <MenuItem value="Sud">Sud</MenuItem>
                    </Select>

                    {/* Recherche Nom */}
                    <TextField 
                        size="small" 
                        placeholder="Rechercher un collaborateur..." 
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        sx={{ bgcolor: "#f1f5f9", borderRadius: 2, "& fieldset": { border: "none" }, minWidth: 250 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled"/></InputAdornment> }}
                    />

                    <Box sx={{ flexGrow: 1 }} />

                    <Button 
                        variant="contained" 
                        startIcon={<AddCircleIcon />} 
                        onClick={() => { setEditId(""); setOpenModal(true); }}
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, px: 3 }}
                    >
                        Ajouter un salarié
                    </Button>
                </Box>
            </Box>

            {/* --- LISTE DES DONNÉES --- */}
            <Box sx={{ mb: 12 }}>
                <SalariesData
                    salariesData={salariesData.filter(s => `${s.firstname} ${s.lastname}`.toLowerCase().includes(searchName.toLowerCase()))}
                    salarialMatrice={salarialMatrice}
                    forecastDates={forecastDates}
                    selectedSalaries={selectedSalaries}
                    setSelectedSalaries={setSelectedSalaries}
                    setEditId={setEditId}
                    setOpenModal={setOpenModal}
                />
            </Box>

            {/* --- DOCK FLOTTANT (Identique à Turnover) --- */}
            <Box sx={{ 
                position: "fixed", bottom: 30, right: "50%", transform: "translateX(50%)", 
                display: "flex", alignItems: "center", gap: 2, 
                bgcolor: "white", p: "12px 24px", borderRadius: "100px", border: "1px solid #e2e8f0", 
                boxShadow: "0px 10px 40px rgba(0,0,0,0.1)", zIndex: 1100 
            }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mr: 1 }}>{selectedSalaries.length} sélectionnés</Typography>
                
                <Tooltip title="Supprimer">
                    <Fab color="error" size="small" disabled={selectedSalaries.length === 0} onClick={() => setShowDeleteDialog(true)}>
                        <DeleteIcon fontSize="small" />
                    </Fab>
                </Tooltip>

                <Tooltip title="Dupliquer">
                    <Fab color="info" size="small" disabled={selectedSalaries.length !== 1} onClick={handleDuplication} sx={{color: 'white'}}>
                        <ContentCopyIcon fontSize="small" />
                    </Fab>
                </Tooltip>

                <Divider orientation="vertical" flexItem />

                <Fab color="primary" size="medium" onClick={() => { setEditId(""); setOpenModal(true); }}>
                    <AddCircleIcon />
                </Fab>
            </Box>

            {/* MODALES REGROUPÉES */}
            <SalariesModals 
                openModal={openModal}
                setOpenModal={setOpenModal}
                showConfirmDialog={showConfirmDialog}
                setShowConfirmDialog={setShowConfirmDialog}
                openMatriceModal={openMatriceModal}
                setOpenMatriceModal={setOpenMatriceModal}
                showDeleteDialog={showDeleteDialog}
                setShowDeleteDialog={setShowDeleteDialog}
                editId={editId}
                setEditId={setEditId}
                forecastVersion={forecastVersion}
                forecastDates={forecastDates}
                confirmDelete={confirmDelete}
                selectedSalaries={selectedSalaries}
                salarialMatrice={salarialMatrice}
            />

            {/* ALERTES DE CONFIRMATION (Style Turnover) */}
       

        </Box>
    );
};

export default Salaries;